import {
  issueQueryChipTokens,
  setAssigneeToken,
  setLabelToken,
  setPriorityToken,
  setStatusToken,
} from '@/lib/issue-query';
import { PRIORITY_LABELS, STATUS_LABELS } from '@/lib/linear-utils';
import type { IssueStatus } from '@/types/issue';
import type { IssueLabel } from '@/types/label';
import type { SharedPageProps } from '@/types/shared';
import { usePage } from '@inertiajs/react';
import { ListFilter, X } from 'lucide-react';
import { useState } from 'react';

interface Member {
  id: string;
  name: string;
  emailAddress?: string;
}

interface Props {
  q: string;
  onChange: (q: string) => void;
  labels?: IssueLabel[];
}

const PRIORITY_WORDS: Record<string, string> = {
  '0': 'none',
  '1': 'urgent',
  '2': 'high',
  '3': 'medium',
  '4': 'low',
  none: 'none',
  urgent: 'urgent',
  high: 'high',
  medium: 'medium',
  low: 'low',
};

export default function FilterBar({ q, onChange, labels = [] }: Props) {
  const { members = [], currentUser } = usePage<
    SharedPageProps & {
      members?: Member[];
    }
  >().props;
  const [open, setOpen] = useState(false);
  const chips = issueQueryChipTokens(q);

  const chipLabel = (token: string): string => {
    const [key, value] = token.split(':', 2);
    if (token === 'is:unassigned' || (key === 'assignee' && value === 'unassigned')) {
      return 'Assignee is Unassigned';
    }
    if (key === 'assignee' && value === 'me') return 'Assignee is Me';
    if (key === 'assignee') {
      const member = members.find(
        (item) => item.emailAddress?.toLowerCase() === value.toLowerCase()
      );
      return `Assignee is ${member?.name ?? value}`;
    }
    if (key === 'status') {
      return `Status is ${STATUS_LABELS[value as IssueStatus] ?? value}`;
    }
    if (key === 'priority') {
      const word = PRIORITY_WORDS[value.toLowerCase()] ?? value;
      const numeric = Object.entries(PRIORITY_WORDS).find(([, label]) => label === word)?.[0];
      return `Priority is ${PRIORITY_LABELS[Number(numeric)] ?? word}`;
    }
    if (key === 'label') return `Label is ${value}`;
    return token;
  };

  const clearToken = (token: string) => {
    const [key] = token.split(':', 2);
    if (token === 'is:unassigned' || key === 'assignee') {
      onChange(setAssigneeToken(q, null));
      return;
    }
    if (key === 'status') {
      onChange(setStatusToken(q, null));
      return;
    }
    if (key === 'priority') {
      onChange(setPriorityToken(q, null));
      return;
    }
    if (key === 'label') {
      onChange(setLabelToken(q, null));
      return;
    }
    onChange(q.replace(token, ' ').replace(/\s+/g, ' ').trim());
  };

  return (
    <div className="ln-filter-bar">
      {chips.map((token) => (
        <button
          key={token}
          type="button"
          className="ln-filter-chip"
          onClick={() => clearToken(token)}
        >
          {chipLabel(token)}
          <X size={12} />
        </button>
      ))}
      <div style={{ position: 'relative' }}>
        <button type="button" className="ln-toolbar-btn" onClick={() => setOpen((value) => !value)}>
          <ListFilter size={14} />
          Filter
        </button>
        {open ? (
          <div className="ln-popover" style={{ top: 32, right: 0, minWidth: 240, zIndex: 20 }}>
            <div className="ln-palette-group">Assignee</div>
            <button
              type="button"
              className="ln-popover-item"
              onClick={() => {
                onChange(setAssigneeToken(q, 'assignee:me'));
                setOpen(false);
              }}
            >
              Me
            </button>
            <button
              type="button"
              className="ln-popover-item"
              onClick={() => {
                onChange(setAssigneeToken(q, 'is:unassigned'));
                setOpen(false);
              }}
            >
              Unassigned
            </button>
            {members
              .filter((member) => member.id !== currentUser?.id)
              .map((member) => (
                <button
                  key={member.id}
                  type="button"
                  className="ln-popover-item"
                  onClick={() => {
                    if (member.emailAddress) {
                      onChange(setAssigneeToken(q, `assignee:${member.emailAddress}`));
                    }
                    setOpen(false);
                  }}
                >
                  {member.name}
                </button>
              ))}
            <div className="ln-palette-group">Status</div>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className="ln-popover-item"
                onClick={() => {
                  onChange(setStatusToken(q, value));
                  setOpen(false);
                }}
              >
                {label}
              </button>
            ))}
            <div className="ln-palette-group">Priority</div>
            {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className="ln-popover-item"
                onClick={() => {
                  onChange(setPriorityToken(q, PRIORITY_WORDS[value] ?? value));
                  setOpen(false);
                }}
              >
                {label}
              </button>
            ))}
            {labels.length > 0 ? (
              <>
                <div className="ln-palette-group">Label</div>
                {labels.map((label) => (
                  <button
                    key={label.id}
                    type="button"
                    className="ln-popover-item"
                    onClick={() => {
                      onChange(setLabelToken(q, label.name));
                      setOpen(false);
                    }}
                  >
                    {label.name}
                  </button>
                ))}
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
